import React, { useEffect, useMemo, useState } from 'react';
import { listMarketsForLens, MarketCard, MockMarketStatus, subscribeMarketActivity } from '../lib/mockMarkets';
import {
  confidenceBand,
  confidenceLabel,
  LENS_OPTIONS,
  RegionLens,
  starRating,
} from '../lib/socialPulse';

// Multi-Market Lens Grid (PROTOCOL_SPEC.md §5).
//
// Rendered in place of the live AMM ward feed when the active lens
// is anything other than LOCAL. Each card is a preview surface —
// pledges don't clear against real balances. The grid inherits the
// §4 CS chrome (stars, gold ≥80% badge, red <30% flag) so testers
// experience the same Terminal shape as the live feed.

const LENS_ACCENT: Record<RegionLens, string> = {
  LOCAL:  '#7dd3fc',
  GLOBAL: '#fcd34d',
  ASIA:   '#f472b6',
  EUROPE: '#a78bfa',
};

const BAND_ACCENT: Record<ReturnType<typeof confidenceBand>, string> = {
  verified: '#fcd34d',
  volatile: '#fca5a5',
  neutral:  '#64748b',
};

function StarStrip({ score, volume }: { score: number; volume?: number }): React.JSX.Element {
  const filled = starRating(score);
  const accent = BAND_ACCENT[confidenceBand(score)];
  // v1.8 Tightening: stars shrunk from 10px → 8px with slightly tighter
  // letter-spacing so the glyphs sit closer to the CS pct on a denser grid.
  //
  // v2.2 Truth Pulse: glow intensity scales logarithmically with the
  // market's total_pledged_volume (volumeOC here). Markets at ≤100 OC
  // get a near-zero glow; a 20k OC high-volume market maxes the pulse.
  // Non-verified bands stay quiet — gold/amber is the only tier the
  // pulse feels appropriate on so a crowded terminal doesn't shimmer.
  const band = confidenceBand(score);
  const pulseOn = band === 'verified' && typeof volume === 'number' && volume > 0;
  const intensity = pulseOn
    ? Math.min(1, Math.log10(Math.max(10, (volume as number))) / 4.3) // log₁₀(20000) ≈ 4.3
    : 0;
  const glowRgba = `rgba(252,211,77,${(0.25 + 0.55 * intensity).toFixed(3)})`;
  return (
    <span
      className={pulseOn ? 'truth-pulse' : undefined}
      style={{
        fontSize:      8,
        letterSpacing: '0.02em',
        color:         accent,
        ['--truth-pulse-glow' as any]: glowRgba,
      }}
    >
      {'★'.repeat(filled)}
      <span style={{ color: '#333' }}>{'★'.repeat(5 - filled)}</span>
    </span>
  );
}

function LensPills({ regions }: { regions: RegionLens[] }): React.JSX.Element {
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {regions.map(region => (
        <span key={region} style={{
          color:         LENS_ACCENT[region],
          fontSize:      7,
          letterSpacing: '0.18em',
          fontWeight:    800,
          padding:       '1px 5px',
          border:        `1px solid ${LENS_ACCENT[region]}`,
          borderRadius:  3,
        }}>
          {region}
        </span>
      ))}
    </span>
  );
}

function VerifiedBadge(): React.JSX.Element {
  return (
    <span style={{
      display:         'inline-flex',
      alignItems:      'center',
      gap:             4,
      padding:         '1px 6px',
      borderRadius:    3,
      border:          '1px solid #fcd34d',
      backgroundColor: 'rgba(252,211,77,0.08)',
      color:           '#fcd34d',
      fontSize:        7,
      letterSpacing:   '0.22em',
      fontWeight:      800,
      textShadow:      '0 0 4px rgba(252,211,77,0.35)',
    }}>
      ✦ CITIZEN VERIFIED
    </span>
  );
}

function ImpliedBar({ yes }: { yes: number }): React.JSX.Element {
  // Opposing bars — YES (emerald) grows right, NO (slate) grows left.
  // Sums to 100%; visual anchor is the centre split.
  const yesPct = Math.round(yes * 100);
  const noPct  = 100 - yesPct;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{
        display:        'flex',
        alignItems:     'center',
        gap:            6,
        fontSize:       9,
        letterSpacing:  '0.14em',
      }}>
        <span style={{ color: '#86efac', fontWeight: 700 }}>YES {yesPct}%</span>
        <span style={{ flex: 1 }} />
        <span style={{ color: '#94a3b8', fontWeight: 700 }}>{noPct}% NO</span>
      </div>
      <div style={{
        display:         'flex',
        height:          4,
        borderRadius:    2,
        backgroundColor: '#1e293b',
        overflow:        'hidden',
      }}>
        <div style={{ width: `${yesPct}%`, backgroundColor: '#22c55e' }} />
        <div style={{ width: `${noPct}%`,  backgroundColor: '#334155' }} />
      </div>
    </div>
  );
}

// Visual palette for the three non-ACTIVE lifecycle states. Kept in one
// place so the badge, border, and overlay tint stay colour-consistent
// across the card. Matches the Mint Queue palette so a tester flipping
// between the Queue and the grid reads the same meaning from both.
const STATUS_META: Record<MockMarketStatus, {
  label: string; color: string; border: string; overlay: string; body: string;
}> = {
  STALE: {
    label:   'STALE · ORACLE NO-SIGNAL',
    color:   '#fca5a5',
    border:  '#7f1d1d',
    overlay: 'rgba(127,29,29,0.22)',
    body:    'Pledges frozen. Awaits Sovereign Veto (T+24h) or Auto-Refund (T+72h).',
  },
  VOIDED_BY_SOVEREIGN: {
    label:   'VOIDED BY SOVEREIGN',
    color:   '#f87171',
    border:  '#450a0a',
    overlay: 'rgba(69,10,10,0.32)',
    body:    'Bond + Maker Fee burned. Pledgers refunded 1:1.',
  },
  AUTO_REFUNDED: {
    label:   'AUTO-REFUNDED',
    color:   '#86efac',
    border:  '#14532d',
    overlay: 'rgba(20,83,45,0.22)',
    body:    'Bond returned. Maker Fee forfeited. Pledgers refunded 1:1.',
  },
};

function StatusBadge({ status }: { status: MockMarketStatus }): React.JSX.Element {
  const meta = STATUS_META[status];
  return (
    <span style={{
      color:           meta.color,
      backgroundColor: meta.overlay,
      fontSize:        7,
      letterSpacing:   '0.22em',
      fontWeight:      800,
      padding:         '2px 7px',
      borderRadius:    3,
      border:          `1px solid ${meta.color}`,
    }}>
      {meta.label}
    </span>
  );
}

interface CardProps { m: MarketCard; }

function Card({ m }: CardProps): React.JSX.Element {
  const pct       = Math.round(m.cs * 100);
  const band      = confidenceBand(m.cs);
  const accent    = BAND_ACCENT[band];
  const label     = confidenceLabel(m.cs);
  const deltaCol  = m.deltaPct >= 0 ? '#86efac' : '#fca5a5';
  const deltaSym  = m.deltaPct >= 0 ? '▲' : '▼';
  const statusMeta = m.status ? STATUS_META[m.status] : null;
  const borderCol =
    statusMeta         ? statusMeta.border :
    band === 'verified' ? '#78350f' :
    band === 'volatile' ? '#450a0a' :
    '#1e293b';
  // Dim the whole card when it isn't ACTIVE so the terminal chrome reads
  // at a glance — the stale/voided/refunded markets are visibly "off the
  // board" without being removed from it.
  const cardOpacity = statusMeta ? 0.72 : 1;
  return (
    <div style={{
      backgroundColor: '#0f172a',
      border:          `1px solid ${borderCol}`,
      borderRadius:    8,
      // v1.8 Tightening: padding shrunk 20% (12×14 → 10×11) and inner
      // gap from 10 → 8 so the same card renders ~20% tighter.
      padding:         '10px 11px',
      display:         'flex',
      flexDirection:   'column',
      gap:             8,
      boxShadow:       band === 'verified' && !statusMeta ? '0 0 14px rgba(252,211,77,0.12)' : undefined,
      fontFamily:      'monospace',
      opacity:         cardOpacity,
      position:        'relative',
    }}>
      {/* Header — lens pills + stars + CS pct */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <LensPills regions={m.regions} />
        <span style={{ flex: 1 }} />
        <StarStrip score={m.cs} volume={m.volumeOC} />
        <span style={{ color: accent, fontSize: 11, fontWeight: 700 }}>{pct}%</span>
      </div>

      {/* Question */}
      <p style={{
        color:      '#e2e8f0',
        fontSize:   12,
        lineHeight: 1.35,
        margin:     0,
        minHeight:  34,       // reserve room so a 1-line vs 2-line title doesn't shift the bar below
      }}>
        {m.title}
      </p>

      <ImpliedBar yes={m.impliedYes} />

      {/* Metrics + badge strip */}
      <div style={{
        display:       'flex',
        alignItems:    'center',
        gap:           8,
        paddingTop:    6,
        // v1.8 Tightening — high-contrast separator (#333) over slate-800.
        borderTop:     '1px dashed #333',
        fontSize:      8,
        letterSpacing: '0.1em',
      }}>
        <span style={{ color: '#475569' }}>
          VOL <span style={{ color: '#94a3b8' }}>{m.volumeOC.toLocaleString()} OC</span>
        </span>
        <span style={{ color: '#334155' }}>·</span>
        <span style={{ color: deltaCol, fontWeight: 700 }}>
          {deltaSym} {Math.abs(m.deltaPct).toFixed(2)}%
        </span>
        <span style={{ flex: 1 }} />
        {m.status
          ? <StatusBadge status={m.status} />
          : band === 'verified'
            ? <VerifiedBadge />
            : band === 'volatile'
              ? <span style={{
                  color:         '#fca5a5',
                  fontSize:      7,
                  letterSpacing: '0.22em',
                  fontWeight:    800,
                }}>{label}</span>
              : null}
      </div>

      {statusMeta && (
        <p style={{
          margin:          0,
          color:           statusMeta.color,
          fontSize:        8,
          letterSpacing:   '0.12em',
          lineHeight:      1.4,
          padding:         '6px 8px',
          borderRadius:    4,
          backgroundColor: statusMeta.overlay,
          border:          `1px dashed ${statusMeta.color}`,
        }}>
          {statusMeta.body}
        </p>
      )}
    </div>
  );
}

interface Props { lens: RegionLens; }

export default function PledgeCardGrid({ lens }: Props): React.JSX.Element {
  // Tick counter re-runs the memo when the mock background activity
  // mutates the pool. Subscribing only while mounted keeps the React
  // tree aligned with the module-level pool.
  const [tick, setTick] = useState(0);
  useEffect(() => subscribeMarketActivity(() => setTick(t => t + 1)), []);
  const markets = useMemo(() => listMarketsForLens(lens), [lens, tick]);
  const lensLabel = LENS_OPTIONS.find(o => o.value === lens)?.label ?? lens;

  return (
    <div style={{
      backgroundColor: '#0b1220',
      // v1.8 Tightening: thinner, higher-contrast shell border (#333)
      // over the prior #1e293b so the Lens Grid reads as a Bloomberg-
      // style framed panel rather than a faint navy vignette.
      border:          '1px solid #333',
      borderRadius:    8,
      padding:         '14px 16px',
      fontFamily:      'monospace',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <p style={{ color: '#475569', fontSize: 9, letterSpacing: '0.18em', margin: 0 }}>
          LENS GRID · {lensLabel.toUpperCase()}
        </p>
        <span style={{ color: '#334155', fontSize: 8, letterSpacing: '0.12em' }}>
          {markets.length} MARKET{markets.length === 1 ? '' : 'S'}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{
          color:           '#78350f',
          fontSize:        7,
          letterSpacing:   '0.22em',
          fontWeight:      800,
          padding:         '1px 6px',
          border:          '1px solid #78350f',
          borderRadius:    3,
          backgroundColor: 'rgba(120,53,15,0.12)',
        }}>
          SANDBOX · PREVIEW
        </span>
      </div>

      {markets.length === 0 ? (
        <p style={{ color: '#334155', fontSize: 11, textAlign: 'center', padding: '20px 0', margin: 0 }}>
          No markets under this lens — try another region.
        </p>
      ) : (
        <div style={{
          display:             'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap:                 10,
        }}>
          {markets.map(m => <Card key={m.id} m={m} />)}
        </div>
      )}

      <p style={{
        color:         '#1e293b',
        fontSize:      9,
        letterSpacing: '0.1em',
        margin:        '12px 0 0',
        textAlign:     'center',
      }}>
        Cards sorted by confidence, then volume. Preview pledges do not clear against balances.
      </p>
    </div>
  );
}
